import {
	CAMPAIGN_ROUTING_FIELD_OPERATORS,
	CAMPAIGN_ROUTING_SCORING_FIELDS,
	CAMPAIGN_ROUTING_VALUE_OPERATORS,
	CampaignRoutingCondition,
	CampaignRoutingField,
	CampaignRoutingOperator,
} from "@signalist/shared-types";

/** The prefix of every routing translation key (`enCampaignRouting.ts`). */
export const ROUTING_I18N = "list.outreach.routing";

/** The field dropdown's groups, in the order the editor offers them (mockup frame 3). */
export const CAMPAIGN_ROUTING_FIELD_GROUPS: ReadonlyArray<{
	labelKey: string;
	fields: readonly CampaignRoutingField[];
}> = [
	{
		labelKey: `${ROUTING_I18N}.fields.groupLead`,
		fields: ["PERSONAL_COUNTRY", "SENIORITY", "JOB_TITLE", "PROFILE_LANGUAGE"],
	},
	{
		labelKey: `${ROUTING_I18N}.fields.groupCompany`,
		fields: ["COMPANY_COUNTRY", "INDUSTRY", "NUMBER_OF_EMPLOYEES"],
	},
	{
		labelKey: `${ROUTING_I18N}.fields.groupScoring`,
		fields: ["PERSONA_SEGMENT", "COMPANY_SEGMENT", "PRIORITY"],
	},
];

/** The translation key of a field's label: a closed map, never the key itself (web i18n). */
export const fieldLabelKey = (field: CampaignRoutingField): string =>
	`${ROUTING_I18N}.fields.${field}`;

/** The translation key of an operator's label. */
export const operatorLabelKey = (operator: CampaignRoutingOperator): string =>
	`${ROUTING_I18N}.operators.${operator}`;

/** The operators the editor offers for a field: exactly the shared contract's. */
export const operatorsFor = (field: CampaignRoutingField): readonly CampaignRoutingOperator[] =>
	CAMPAIGN_ROUTING_FIELD_OPERATORS[field];

/** Whether the operator compares against values (and needs at least one). */
export const takesValues = (operator: CampaignRoutingOperator): boolean =>
	CAMPAIGN_ROUTING_VALUE_OPERATORS.includes(operator);

/** Whether the field reads this company's lead scoring (a lead without a score never matches). */
export const isScoringField = (field: CampaignRoutingField): boolean =>
	CAMPAIGN_ROUTING_SCORING_FIELDS.includes(field);

/**
 * Where a field's values come from in the editor:
 * - `lookup`: the lead-table filter lookup (`list/filterByProperty/:property`), loaded up front
 *   for the short fixed lists (`eager`) and per typed search for the long ones;
 * - `language`: the language list the language rules use (no filter lookup exists);
 * - `text`: free words for Job title; `range`: from/to for Number of employees.
 */
export type RoutingValueSource =
	| { kind: "lookup"; property: string; eager: boolean }
	| { kind: "language" }
	| { kind: "text" }
	| { kind: "range" };

export const valueSourceOf = (field: CampaignRoutingField): RoutingValueSource => {
	switch (field) {
		case "PERSONAL_COUNTRY":
			return { kind: "lookup", property: "country", eager: false };
		case "COMPANY_COUNTRY":
			return { kind: "lookup", property: "companyCountry", eager: false };
		case "INDUSTRY":
			return { kind: "lookup", property: "industry", eager: false };
		case "PERSONA_SEGMENT":
			return { kind: "lookup", property: "personaCategory", eager: true };
		case "COMPANY_SEGMENT":
			return { kind: "lookup", property: "companyCategory", eager: true };
		case "PRIORITY":
			return { kind: "lookup", property: "overallScore", eager: true };
		case "SENIORITY":
			return { kind: "lookup", property: "seniority", eager: true };
		case "PROFILE_LANGUAGE":
			return { kind: "language" };
		case "JOB_TITLE":
			return { kind: "text" };
		case "NUMBER_OF_EMPLOYEES":
			return { kind: "range" };
		default: {
			const unknownField: never = field;

			throw new Error(`Unknown routing field ${String(unknownField)}`);
		}
	}
};

/** A fresh condition on a field: its first operator, nothing picked yet. */
export const newCondition = (
	field: CampaignRoutingField = "PERSONAL_COUNTRY"
): CampaignRoutingCondition => ({ field, operator: operatorsFor(field)[0], values: [] });

/**
 * The condition after the user picks another field. The operator stays when the new field takes it;
 * the values stay only when they mean the same thing on the new field (never: a country is not an
 * industry), so a switch starts clean.
 */
export const withField = (
	condition: CampaignRoutingCondition,
	field: CampaignRoutingField
): CampaignRoutingCondition => {
	if (field === condition.field) {
		return condition;
	}

	const operator = operatorsFor(field).includes(condition.operator)
		? condition.operator
		: operatorsFor(field)[0];

	return { field, operator, values: [] };
};

/** The condition after the user picks another operator: values kept while the new one takes them. */
export const withOperator = (
	condition: CampaignRoutingCondition,
	operator: CampaignRoutingOperator
): CampaignRoutingCondition => {
	if (operator === "BETWEEN") {
		return {
			field: condition.field,
			operator,
			from: condition.from ?? null,
			to: condition.to ?? null,
		};
	}

	return {
		field: condition.field,
		operator,
		values: takesValues(operator) ? (condition.values ?? []) : [],
	};
};
